import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { useContent } from '@eneco/cms';
import { useRouter } from '@eneco/next';

import type { UsabillaFeedbackContent } from './usabilla-feedback-content.types';

/**
 * `UsabillaFeedback` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/system/src/UsabillaFeedback/
 * UsabillaFeedback.tsx`): an invisible `ub-in-page` marker div the Usabilla script targets, cleared on
 * route change (in-page navigation never re-renders it) and patched against a `display: contents`
 * ancestor sizing bug.
 */
const UsabillaFeedback: FC = () => {
  const content = useContent<UsabillaFeedbackContent>();
  const element = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Make sure only 1 instance rendered
  useEffect(() => {
    const ubElement = element.current;
    if (!ubElement || typeof window === 'undefined') return;

    // Clear the element on route change, this is essential for in page navigation where the element is not re-rendered
    router.events.on('routeChangeStart', () => {
      if (ubElement?.childNodes?.length) {
        while (ubElement.firstChild) {
          ubElement.firstChild.remove();
        }
      }
    });
  }, [router]);

  useEffect(() => {
    const ubElement = element.current;
    if (!ubElement) return;

    /**
     * Usabilla traverses the parents of the `ub-in-page` element and checks their `getBoundingClientRect`
     * dimensions to set the dimensions of its iframe. As elements with `display: contents` return
     * dimensions with value `0`, the iframe is displayed with the same dimensions.
     *
     * By overwriting the `getBoundingClientRect` method of these elements with their parent's (which is
     * exactly what they display as), this Usabilla issue is fixed.
     */
    let parent = ubElement.parentElement;
    while (parent?.parentElement) {
      if (window.getComputedStyle(parent)?.display === 'contents') {
        const grandParent = parent.parentElement;
        parent.getBoundingClientRect = () => grandParent.getBoundingClientRect();
      }
      parent = parent.parentElement;
    }
  }, [element]);

  const usabillaAttributes = {
    'ub-in-page': content.inPageSurveyIdText,
    'ub-in-page-category': content.inPageCategoryText,
    'ub-in-page-subcategory': content.inPageSubCategoryText,
    'ub-in-page-item': content.inPageSurveyItemText,
  };

  return <div {...usabillaAttributes} ref={element} />;
};

export default UsabillaFeedback;
